import { useEffect, useRef, useState } from "react";
import { fetchSheetPatients } from "../services/sheetPatientsService";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { getConsentsPath, getDuplicatesPath, getStudiesPath } from "../services/firestorePaths";
import { activeSiteUsesLegacySheet } from "../services/activeTenant";
import { loadCoordinatorNotes } from "../services/patientQueryNotes";
import {
  notEligibleReasonText,
  notesContain,
  notEligibleContains,
} from "../services/patientQueryNotesMatch";
import { DIAGNOSIS_OPTIONS, diagnosisLabel, statusLabel } from "../constants/diseaseOptions";
import { CRC_BOX_STATUSES } from "../constants/patientBoxes";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiDownload,
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiUser,
  FiFileText,
  FiPlus,
  FiX,
} from "react-icons/fi";

import {
  MdScience,
  MdOutlineMedication
} from "react-icons/md";

const bannerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

const operators = ["=", ">", "<", ">=", "<=", "contains"];

// Fields grouped into categories so the (long) selector reads as an organized,
// professional query builder rather than one flat list.
const FIELD_GROUPS = [
  {
    group: "Profile",
    items: [
      { key: "diagnosis", label: "Diagnosis" },
      { key: "status", label: "Status" },
      { key: "study", label: "Study" },
      // Legacy notes + coordinator notes timeline + replies (for "contains").
      { key: "notes", label: "Notes" },
      // The reason typed when a patient is marked Not Eligible for a study.
      { key: "notEligibleReason", label: "Not Eligible Reason" },
      { key: "mrn", label: "MRN" },
      { key: "age", label: "Age" },
    ],
  },
  {
    group: "Spirometry",
    items: [
      { key: "fev1Pre", label: "FEV1 Pre" },
      { key: "fev1Post", label: "FEV1 Post" },
      { key: "fvcPre", label: "FVC Pre" },
      { key: "fvcPost", label: "FVC Post" },
      { key: "fev1fvcPre", label: "FEV1/FVC Ratio Pre" },
      { key: "fev1fvcPost", label: "FEV1/FVC Ratio Post" },
      { key: "revPercent", label: "Reversibility %" },
      { key: "methResult", label: "Methacholine Result" },
    ],
  },
  {
    group: "Biomarkers & Vitals",
    items: [
      { key: "bmi", label: "BMI" },
      { key: "ahi", label: "AHI" },
      { key: "feno", label: "FENO" },
      { key: "eos", label: "EOS" },
    ],
  },
  {
    group: "Oxygen & Comorbidities",
    items: [
      { key: "oxygenLitres", label: "Oxygen Liters" },
      { key: "oxygenType", label: "Oxygen Type" },
      { key: "diabetes", label: "Diabetes" },
      { key: "cancer", label: "Cancer" },
    ],
  },
];
const dropdownOptions = {
  // Stored values — rule matching compares these; the dropdown shows labels
  // ("IPF" as "ILD"), so an ILD query matches every stored IPF patient.
  diagnosis: DIAGNOSIS_OPTIONS,

  // Every CRC-page box, in the board's order (Future Studies after Deceased),
  // from the shared list so a new box is queryable automatically. Stored
  // values; the dropdown shows statusLabel ("Future Studies - IPF" as ILD).
  status: [
    ...CRC_BOX_STATUSES.flatMap((s) =>
      s === "Deceased" ? [s, ...DIAGNOSIS_OPTIONS.map((dx) => `Future Studies - ${dx}`)] : [s]
    ),
    "Potential Duplicate",
  ],

  oxygenType: [
    "PRN",
    "Night",
    "Continuous"
  ],

  diabetes: [
    "Yes",
    "No"
  ],

  cancer: [
    "Yes",
    "No"
  ]
};

const normalizeDiagnosis = (dx) =>
  Array.isArray(dx) ? dx : dx ? [dx] : [];

// Stored values — used by the "contains" rule matching; don't relabel here.
const formatDiagnosis = (dx) =>
  Array.isArray(dx) ? dx.join(", ") : dx || "-";

// Display / export only.
const formatDiagnosisLabel = (dx) =>
  Array.isArray(dx) ? dx.map(diagnosisLabel).join(", ") : diagnosisLabel(dx) || "-";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const Section = ({
  title,
  icon,
  accent = "from-purple-400 to-blue-400",
  compact = false,
  compactTitle,
  children,
}) => {
  const [open, setOpen] = useState(true);

  if (compact) {
    return (
      <div className="relative min-h-[82px] rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-2xl overflow-hidden px-2 py-3 flex flex-col items-center justify-center gap-2 text-center">
        <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${accent} opacity-70 pointer-events-none`} />
        <div className="w-8 h-8 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <span className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-200 leading-tight">
          {compactTitle || title}
        </span>
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-visible">
      <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${accent} opacity-70 pointer-events-none`} />
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.04] via-transparent to-blue-500/[0.04] pointer-events-none" />
      <button
        onClick={() => setOpen(!open)}
        className="relative w-full flex items-center gap-2 px-4 py-3 group"
        aria-expanded={open}
      >
        {icon && (
          <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:border-purple-300/30 transition shrink-0">
            {icon}
          </div>
        )}
        <div className="text-left min-w-0">
          <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">
            {title}
          </h2>
        </div>
        <span className="ml-2 h-px flex-1 bg-white/5" />
        <span className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-slate-300 group-hover:text-white transition shrink-0">
          {open ? (
            <FiChevronUp className="w-3.5 h-3.5" />
          ) : (
            <FiChevronDown className="w-3.5 h-3.5" />
          )}
        </span>
      </button>

      {open && <div className="relative px-4 pb-4 grid gap-3">{children}</div>}
    </div>
  );
};

const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition";
const inputDateCls = `${inputCls} [color-scheme:dark]`;
const selectCls =
  "w-full appearance-none px-3 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition";

const STATUS_TONE = {
  enrolled: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
  "screen failed": "bg-orange-400/15 text-orange-200 border-orange-400/30",
  "unable to reach": "bg-amber-400/15 text-amber-200 border-amber-400/30",
  "patient scheduled for screen": "bg-blue-400/15 text-blue-200 border-blue-400/30",
  "ready to schedule": "bg-yellow-400/15 text-yellow-200 border-yellow-400/30",
  "potential duplicate": "bg-rose-400/15 text-rose-200 border-rose-400/30",
  deceased: "bg-slate-400/15 text-slate-300 border-slate-400/30",
};

const getStatusToneCls = (status) => {
  if (!status) return "bg-white/[0.05] text-slate-300 border-white/10";
  return (
    STATUS_TONE[status.toLowerCase()] ||
    "bg-white/[0.05] text-slate-300 border-white/10"
  );
};

const DX_TONE = {
  asthma: "bg-indigo-400/15 text-indigo-200 border-indigo-400/30",
  copd: "bg-orange-400/15 text-orange-200 border-orange-400/30",
  bronchiectasis: "bg-purple-400/15 text-purple-200 border-purple-400/30",
  "sleep apnea": "bg-teal-400/15 text-teal-200 border-teal-400/30",
  ipf: "bg-rose-400/15 text-rose-200 border-rose-400/30",
  diagnostics: "bg-sky-400/15 text-sky-200 border-sky-400/30",
  obesity: "bg-fuchsia-400/15 text-fuchsia-200 border-fuchsia-400/30",
  diabetes: "bg-amber-400/15 text-amber-200 border-amber-400/30",
  sarcoid: "bg-lime-400/15 text-lime-200 border-lime-400/30",
  "heart disease": "bg-red-400/15 text-red-200 border-red-400/30",
  cancer: "bg-pink-400/15 text-pink-200 border-pink-400/30",
};

const getDxToneCls = (dx) =>
  DX_TONE[(dx || "").toLowerCase()] ||
  "bg-white/[0.05] text-slate-300 border-white/10";

const FILTER_STORAGE_KEY = "patientQueryFilters";
const PatientQueryPage = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [results, setResults] = useState([]);

  const [rules, setRules] = useState(() => {
  const saved = localStorage.getItem(FILTER_STORAGE_KEY);
  if (!saved) {
    return [{ field: "diagnosis", operator: "=", value: "" }];
  }

  try {
    return JSON.parse(saved).rules || [
      { field: "diagnosis", operator: "=", value: "" },
    ];
  } catch {
    return [{ field: "diagnosis", operator: "=", value: "" }];
  }
});

const savedFilters = (() => {
  try {
    return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
})();

const [selectedStudy, setSelectedStudy] = useState(savedFilters.selectedStudy || "");
const [studySearch, setStudySearch] = useState(savedFilters.studySearch || "");

const [exacFrom, setExacFrom] = useState(savedFilters.exacFrom || "");
const [exacTo, setExacTo] = useState(savedFilters.exacTo || "");
const [exacOperator, setExacOperator] = useState(savedFilters.exacOperator || ">=");
const [exacCount, setExacCount] = useState(savedFilters.exacCount || "");
const [studyDropdownOpen, setStudyDropdownOpen] = useState(false);
const [studies, setStudies] = useState([]);
const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const [loading, setLoading] = useState(true);
  const [studyNameByDocId, setStudyNameByDocId] = useState(() => new Map());

  // Coordinator notes (+ replies), read only once a "Notes contains" search is
  // active, then kept for the rest of the visit. null = not loaded yet.
  const [coordNotes, setCoordNotes] = useState(null); // { byPatient, failed }
  const [coordProgress, setCoordProgress] = useState(null); // { done, total }
  const coordNotesStartedRef = useRef(false);
  const activeNotesTerm = (
    rules.find((r) => r.field === "notes" && r.operator === "contains" && String(r.value || "").trim()) || {}
  ).value;
  const activeNotEligibleTerm = (
    rules.find(
      (r) => r.field === "notEligibleReason" && r.operator === "contains" && String(r.value || "").trim()
    ) || {}
  ).value;

  useEffect(() => {
    if (!activeNotesTerm || loading || !patients.length || coordNotesStartedRef.current) return;
    coordNotesStartedRef.current = true;
    const ids = patients.map((p) => p.token);
    setCoordProgress({ done: 0, total: new Set(ids).size });
    loadCoordinatorNotes(ids, {
      onProgress: (done, total) => setCoordProgress({ done, total }),
    }).then((res) => {
      setCoordNotes(res);
      setCoordProgress(null);
    });
  }, [activeNotesTerm, loading, patients]);

  // Where the active notes / not-eligible search matched this patient, for the
  // line under the row. Null when no such search is active or nothing matched.
  const fmtDay = (d) => (d instanceof Date ? d.toLocaleDateString() : "");
  const foundInFor = (p) => {
    if (activeNotesTerm) {
      const hit = notesContain(p.notes, coordNotes?.byPatient.get(p.token) || [], activeNotesTerm);
      if (hit) {
        const label =
          hit.where === "legacy"
            ? "Notes"
            : `${hit.where === "reply" ? "Reply" : "Coordinator note"}${
                hit.createdAt ? ` ${fmtDay(hit.createdAt)}` : ""
              }`;
        return { label, snippet: hit.snippet };
      }
    }
    if (activeNotEligibleTerm) {
      const hit = notEligibleContains(p.studyEligibility, activeNotEligibleTerm);
      if (hit) {
        return {
          label: `Not eligible – ${studyNameByDocId.get(hit.studyId) || hit.studyId}`,
          snippet: hit.snippet,
        };
      }
    }
    return null;
  };

useEffect(() => {
  const filtersToSave = {
    rules,
    selectedStudy,
    studySearch,
    exacFrom,
    exacTo,
    exacOperator,
    exacCount,
  };

  localStorage.setItem(
    FILTER_STORAGE_KEY,
    JSON.stringify(filtersToSave)
  );
}, [
  rules,
  selectedStudy,
  studySearch,
  exacFrom,
  exacTo,
  exacOperator,
  exacCount
]);

const clearFilters = () => {
  localStorage.removeItem(FILTER_STORAGE_KEY);

  setRules([{ field: "diagnosis", operator: "=", value: "" }]);

  setSelectedStudy("");
  setStudySearch("");

  setExacFrom("");
  setExacTo("");
  setExacOperator(">=");
  setExacCount("");
};

  //---------------------------------------------------
  // Normalize patients so ALL sources match
  //---------------------------------------------------
  const calculateAge = (dob) => {
  if (!dob) return 0;

  const birth = new Date(dob);
  const today = new Date();

  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};
  const normalizePatient = (p, source) => {
    let clinical = {};

    if (typeof p.CLINICAL_DATA === "string") {
  try {
    clinical = JSON.parse(p.CLINICAL_DATA);
  } catch {
    clinical = {};
  }
} else if (typeof p.CLINICAL_DATA === "object") {
  clinical = p.CLINICAL_DATA;
} else {
  clinical = {};
}

    return {
  token: p.token || p.id,

  name: p.fullName || p.name || "",
  mrn: p.mrn || "",
    dob: p.dob || "",
  diagnosis: normalizeDiagnosis(p.diagnosis),
  status: p.status || "",
  study: p.study || "",

  notes: p.notes || p.NOTES || "",

  age: Number(clinical.age || p.age || calculateAge(p.dob)),
  bmi: Number(clinical.bmi || p.bmi || 0),
  ahi: Number(clinical.ahi || p.ahi || 0),

  fev1Pre: Number(clinical.fev1Pre || p.fev1Pre || 0),
fev1Post: Number(clinical.fev1Post || p.fev1Post || 0),

fvcPre: Number(clinical.fvcPre || p.fvcPre || 0),
fvcPost: Number(clinical.fvcPost || p.fvcPost || 0),

fev1fvcPre: Number(clinical.fev1fvcPre || p.fev1fvcPre || 0),
fev1fvcPost: Number(clinical.fev1fvcPost || p.fev1fvcPost || 0),

  feno: Number(clinical.fenoPPB || p.fenoPPB || 0),

  revPercent: Number(clinical.revPercent || p.revPercent || 0),

  oxygenLitres: Number(clinical.oxygenLitres || p.oxygenLitres || 0),

  oxygenType: clinical.oxygenType || p.oxygenType || "",

  methResult: clinical.methResult || p.methResult || "",

  diabetes: clinical.diabetes || p.diabetes || "",
  cancer: clinical.cancerStatus || p.cancerStatus || "",

  studyEligibility:
    clinical.studyEligibility || p.studyEligibility || {},
  // "" when there are none, so the rule loop's null guard never drops anyone.
  notEligibleReason: notEligibleReasonText(
    clinical.studyEligibility || p.studyEligibility || {}
  ),

  eos:
    p.eosValues && p.eosValues.length
      ? Number(p.eosValues[p.eosValues.length - 1].value)
      : 0,

  exacerbations: p.exacerbations || [],

  source,
};
  };

  //---------------------------------------------------
  // Load all patients
  //---------------------------------------------------

  useEffect(() => {
    const loadPatients = async () => {
      try {
        // Sheet (Dubois-only legacy store); skip on Firestore-only sites.
        let sheetData = [];
        if (activeSiteUsesLegacySheet()) {
          sheetData = await fetchSheetPatients();
        }

        const sheetPatients = sheetData.map((p) =>
          normalizePatient(p, "sheet")
        );

        // Firestore consents
        const consSnap = await getDocs(collection(db, getConsentsPath()));

        const consents = consSnap.docs.map((d) =>
          normalizePatient({ id: d.id, ...d.data() }, "firestore")
        );

        // Firestore duplicates
        const dupSnap = await getDocs(collection(db, getDuplicatesPath()));

        const duplicates = dupSnap.docs.map((d) =>
          normalizePatient({ id: d.id, ...d.data() }, "duplicates")
        );
        // Load studies collection
const studySnap = await getDocs(collection(db, getStudiesPath()));

// `id` is the study DOC id, because that's what studyEligibility is keyed by
// on the patient page. It used to be STUDY_ID, so the study filter matched
// nobody for any study whose STUDY_ID differs from its doc id (40 of 103 in
// production, 2026-09-25). `code` keeps showing the STUDY_ID staff know.
const studyList = studySnap.docs.map((d) => {
  const s = d.data();
  return {
    id: d.id,
    code: s.STUDY_ID || d.id,
    name: s.NAME || d.id
  };
});
// A study picked on an earlier visit was saved as its STUDY_ID; move it
// onto the doc id so the saved filter keeps working.
setSelectedStudy((prev) => {
  if (!prev || studyList.some((st) => st.id === prev)) return prev;
  const byCode = studyList.find((st) => st.code === prev);
  return byCode ? byCode.id : prev;
});

setStudies(studyList);
// studyEligibility is keyed by study DOC id, so the "Not eligible – <study>"
// snippet needs names by doc id (studyList above uses STUDY_ID).
setStudyNameByDocId(
  new Map(studySnap.docs.map((d) => [d.id, d.data().NAME || d.data().STUDY_ID || d.id]))
);

        const merged = [...sheetPatients, ...consents, ...duplicates];

        setPatients(merged);
        setResults(merged);
      } catch (err) {
        console.error("Patient load error", err);
      }

      setLoading(false);
    };

    loadPatients();

  }, []);

  //---------------------------------------------------
  // Filtering logic
  //---------------------------------------------------

  useEffect(() => {
    let filtered = [...patients];

    filtered = filtered.filter((p) => {

      for (const rule of rules) {
        const val = p[rule.field];

if (val === undefined || val === null) return false;

        if (!rule.value) continue;

        if (rule.operator === "=") {
  if (rule.field === "diagnosis") {
    if (!normalizeDiagnosis(val).includes(rule.value)) return false;
  } else if (String(val) !== rule.value) {
    return false;
  }
}

        if (rule.operator === ">" && Number(val) <= Number(rule.value))
          return false;

        if (rule.operator === "<" && Number(val) >= Number(rule.value))
          return false;

        if (rule.operator === ">=" && Number(val) < Number(rule.value))
          return false;

        if (rule.operator === "<=" && Number(val) > Number(rule.value))
          return false;

        if (rule.operator === "contains") {
  // Notes: legacy notes OR any coordinator note / reply. Other operators on
  // notes keep comparing the legacy field only, exactly as before.
  if (rule.field === "notes") {
    if (!notesContain(val, coordNotes?.byPatient.get(p.token) || [], rule.value))
      return false;
    continue;
  }
  const text =
    rule.field === "diagnosis"
      ? formatDiagnosis(val)
      : String(val || "");

  if (!text.toLowerCase().includes(rule.value.toLowerCase()))
    return false;
}
      }

      if (selectedStudy) {

  const elig = p.studyEligibility || {};

  if (!elig[selectedStudy]) return false;

}
      if (exacCount) {

  const count = (p.exacerbations || []).filter((ex) => {

    if (!ex.date) return false;

    const d = new Date(ex.date);

    return (
      d >= new Date(exacFrom) &&
      d <= new Date(exacTo)
    );

  }).length;

  const target = Number(exacCount);

  if (exacOperator === "=" && count !== target) return false;
  if (exacOperator === ">" && count <= target) return false;
  if (exacOperator === "<" && count >= target) return false;
  if (exacOperator === ">=" && count < target) return false;
  if (exacOperator === "<=" && count > target) return false;

}
      return true;
    });

    setResults(filtered);
}, [
  patients,
  coordNotes,
  rules,
  studySearch,
  selectedStudy,
  exacFrom,
  exacTo,
  exacOperator,
  exacCount
]);
  //---------------------------------------------------
  // Query Builder
  //---------------------------------------------------

  const addRule = () =>
    setRules([...rules, { field: "diagnosis", operator: "=", value: "" }]);

  const updateRule = (i, key, value) => {
    const copy = [...rules];
    copy[i][key] = value;
    setRules(copy);
  };

  const removeRule = (i) => {
    const copy = [...rules];
    copy.splice(i, 1);
    setRules(copy);
  };
const getStatusColor = (status) => {

  if (!status) return "bg-gray-100 text-gray-700";

  if (status === "Enrolled")
    return "bg-green-100 text-green-700";

  if (status === "Screen Failed")
    return "bg-red-100 text-red-700";

  if (status === "Unable to Reach")
    return "bg-yellow-100 text-yellow-700";

  if (status === "Patient Scheduled for Screen")
    return "bg-blue-100 text-blue-700";

  if (status === "Deceased")
    return "bg-slate-200 text-slate-700";

  return "bg-gray-100 text-gray-700";
};

const getDiagnosisColor = (dx) => {

  if (dx === "Asthma")
    return "bg-indigo-100 text-indigo-700";

  if (dx === "COPD")
    return "bg-orange-100 text-orange-700";

  if (dx === "Bronchiectasis")
    return "bg-purple-100 text-purple-700";

  if (dx === "Sleep Apnea")
    return "bg-teal-100 text-teal-700";

  return "bg-gray-100 text-gray-700";
};
  //---------------------------------------------------
  // CSV Export
  //---------------------------------------------------

  const exportCSV = () => {

    if (!results.length) return;

    const rows = results.map((p) => ({
      Name: p.name,
      Diagnosis: formatDiagnosisLabel(p.diagnosis),
      Status: statusLabel(p.status),
      Study: p.study,
      Age: p.age,

      AHI: p.ahi,
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((r) => Object.values(r).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "patient_query_results.csv";

    link.click();
  };


  //---------------------------------------------------
  // Loading screen
  //---------------------------------------------------

  if (loading)
    return (
      <div className="relative min-h-screen w-full text-slate-100 font-[Manrope] flex items-center justify-center">
        <PageBackground />
        <div className="relative flex flex-col items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl blur-md opacity-70" />
            <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
              <FiSearch className="w-5 h-5 text-white animate-pulse" />
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-medium">
            Loading patients…
          </p>
        </div>
      </div>
    );

  //---------------------------------------------------
  // UI
  //---------------------------------------------------

  const activeRuleCount = rules.filter((r) => r.value).length;
  const hasExacFilter = !!exacCount;
  const hasStudyFilter = !!selectedStudy;
  const sidebarGridCls = sidebarExpanded
    ? "lg:grid-cols-[380px_minmax(0,1fr)]"
    : "lg:grid-cols-[104px_minmax(0,1fr)]";

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative max-w-[1700px] mx-auto px-3 sm:px-4 py-3 grid gap-3">
        {/* Sticky top bar */}
        <motion.div
          variants={bannerVariants}
          initial="hidden"
          animate="visible"
          className="sticky top-0 z-40 backdrop-blur-2xl bg-slate-950/55 border border-white/10 rounded-2xl shadow-[0_8px_30px_-12px_rgba(124,58,237,0.35)] px-3 py-2 flex items-center gap-2 sm:gap-3"
        >
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition shrink-0"
          >
            <FiArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                <FiFilter className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                Query RegX
              </div>
              <h1 className="text-sm sm:text-base font-semibold tracking-tight text-white leading-tight truncate">
                Advanced Patient Query Builder
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <span className="hidden md:inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 text-[10px] uppercase tracking-wider text-slate-300">
              <span className="font-semibold text-white">{results.length}</span>
              results
            </span>
          </div>
        </motion.div>

        <div className={`relative grid gap-3 lg:items-start ${sidebarGridCls}`}>
          <aside className="lg:sticky lg:top-[72px] z-30 rounded-2xl border border-white/10 bg-slate-950/50 backdrop-blur-2xl shadow-[0_18px_55px_-24px_rgba(124,58,237,0.65)] p-2 transition-all duration-300">
            <div className={`mb-2 flex items-center gap-2 ${sidebarExpanded ? "justify-between" : "justify-center"}`}>
              {sidebarExpanded && (
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500/25 to-blue-500/25 border border-white/10 flex items-center justify-center shrink-0">
                    <FiFileText className="w-4 h-4 text-purple-200" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-purple-300/80 font-semibold leading-none">
                      Filters
                    </div>
                    <div className="text-xs font-semibold text-white truncate">
                      Query Controls
                    </div>
                  </div>
                </div>
              )}
              <button
                onClick={() => setSidebarExpanded(!sidebarExpanded)}
                aria-label={sidebarExpanded ? "Collapse filters" : "Expand filters"}
                aria-expanded={sidebarExpanded}
                className="w-9 h-9 inline-flex items-center justify-center rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-purple-300/30 text-slate-300 hover:text-white transition shrink-0"
                title={sidebarExpanded ? "Collapse filters" : "Expand filters"}
              >
                <FiChevronDown
                  className={`w-4 h-4 transition-transform ${
                    sidebarExpanded ? "rotate-90" : "-rotate-90"
                  }`}
                />
              </button>
            </div>

            <div className={`grid gap-2 ${sidebarExpanded ? "" : "grid-cols-2 sm:grid-cols-5 lg:grid-cols-1"}`}>
        {/* Study Search */}
        <div className="relative z-10">
        <Section
          title="Study Eligibility Search"
          compact={!sidebarExpanded}
          compactTitle="Study Search"
          icon={<MdScience className="w-4 h-4 text-purple-300" />}
          accent="from-fuchsia-400 via-purple-400 to-blue-400"
        >
          <div className="relative">
            <button
              onClick={() => setStudyDropdownOpen(!studyDropdownOpen)}
              className={`${inputCls} text-left flex items-center justify-between gap-2 cursor-pointer hover:bg-white/[0.06]`}
            >
              <span className={selectedStudy ? "text-slate-100" : "text-slate-500"}>
                {selectedStudy
                  ? studies.find((s) => s.id === selectedStudy)?.code || selectedStudy
                  : "Select Study"}
              </span>
              <FiChevronDown
                className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                  studyDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {studyDropdownOpen && (
              <div className="study-dropdown absolute mt-1.5 left-0 right-0 z-20 rounded-xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(124,58,237,0.5)] max-h-72 overflow-y-auto overscroll-contain">
                <div className="study-dropdown sticky top-0 bg-[#0c0826]/95 backdrop-blur-2xl border-b border-white/10">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search study…"
                      className="w-full pl-9 pr-3 py-2 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                      value={studySearch}
                      onChange={(e) => setStudySearch(e.target.value)}
                    />
                  </div>
                </div>

                <ul className="py-1">
                  {studies
                    .filter((s) =>
                      `${s.name} ${s.code}`
                        .toLowerCase()
                        .includes(studySearch.toLowerCase())
                    )
                    .map((study) => (
                      <li
                        key={study.id}
                        onClick={() => {
                          setSelectedStudy(study.id);
                          setStudySearch(study.code);
                          setStudyDropdownOpen(false);
                        }}
                        className={`px-3 py-2 mx-1 rounded-lg cursor-pointer text-xs transition flex items-center gap-2 ${
                          selectedStudy === study.id
                            ? "bg-purple-500/15 text-white border border-purple-300/30"
                            : "text-slate-200 hover:bg-white/[0.05] border border-transparent"
                        }`}
                      >
                        <MdScience className="w-3 h-3 text-purple-300 shrink-0" />
                        <span className="truncate">
                          {study.name}{" "}
                          <span className="text-slate-500 font-mono">({study.code})</span>
                        </span>
                      </li>
                    ))}
                  {studies.length === 0 && (
                    <li className="px-3 py-2 text-xs text-slate-500">
                      No studies loaded.
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {hasStudyFilter && (
            <button
              onClick={() => {
                setSelectedStudy("");
                setStudySearch("");
              }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[10px] font-medium text-slate-300 hover:text-white transition self-start"
            >
              <FiX className="w-3 h-3" />
              Clear study
            </button>
          )}
        </Section>
        </div>

        {/* Query Builder */}
        <Section
          title={`Advanced Query Builder${
            activeRuleCount ? ` · ${activeRuleCount} active` : ""
          }`}
          compact={!sidebarExpanded}
          compactTitle="Query Builder"
          icon={<FiFilter className="w-4 h-4 text-purple-300" />}
          accent="from-purple-400 via-indigo-400 to-blue-400"
        >
          <div className="space-y-1">
            {rules.map((rule, i) => (
              <div key={i}>
                {/* AND connector between rules */}
                {i > 0 && (
                  <div className="flex items-center gap-2 py-1.5">
                    <span className="h-px flex-1 bg-white/5" />
                    <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-purple-300/70">
                      and
                    </span>
                    <span className="h-px flex-1 bg-white/5" />
                  </div>
                )}

                <div className="relative rounded-xl border border-white/10 bg-white/[0.025] p-2.5 pl-3.5 hover:border-white/20 transition">
                  {/* left accent rail */}
                  <span className="absolute left-0 top-2.5 bottom-2.5 w-[2.5px] rounded-full bg-gradient-to-b from-purple-400/70 to-blue-400/70" />

                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Rule {i + 1}
                    </span>
                    <button
                      onClick={() => removeRule(i)}
                      className="w-6 h-6 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-rose-200 hover:bg-rose-500/15 transition"
                      title="Remove rule"
                    >
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Field (grouped) */}
                  <div className="relative">
                    <select
                      value={rule.field}
                      onChange={(e) => updateRule(i, "field", e.target.value)}
                      className={selectCls}
                    >
                      {FIELD_GROUPS.map((g) => (
                        <optgroup
                          key={g.group}
                          label={g.group}
                          className="bg-[#0b0820] text-slate-100"
                        >
                          {g.items.map((f) => (
                            <option
                              key={f.key}
                              value={f.key}
                              className="bg-[#0b0820] text-slate-100"
                            >
                              {f.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>

                  {/* Operator + value */}
                  <div className="flex gap-1.5 mt-2">
                    <div className="relative w-[92px] shrink-0">
                      <select
                        value={rule.operator}
                        onChange={(e) => updateRule(i, "operator", e.target.value)}
                        className={`${selectCls} pr-7 text-center`}
                      >
                        {operators.map((o) => (
                          <option
                            key={o}
                            value={o}
                            className="bg-[#0b0820] text-slate-100"
                          >
                            {o}
                          </option>
                        ))}
                      </select>
                      <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                    </div>

                    {dropdownOptions[rule.field] ? (
                      <div className="relative flex-1 min-w-0">
                        <select
                          value={rule.value}
                          onChange={(e) => updateRule(i, "value", e.target.value)}
                          className={selectCls}
                        >
                          <option value="" className="bg-[#0b0820] text-slate-100">
                            Select value…
                          </option>
                          {dropdownOptions[rule.field].map((opt) => (
                            <option
                              key={opt}
                              value={opt}
                              className="bg-[#0b0820] text-slate-100"
                            >
                              {rule.field === "diagnosis"
                                ? diagnosisLabel(opt)
                                : rule.field === "status"
                                ? statusLabel(opt)
                                : opt}
                            </option>
                          ))}
                        </select>
                        <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    ) : (
                      <input
                        value={rule.value}
                        onChange={(e) => updateRule(i, "value", e.target.value)}
                        placeholder="Value"
                        className={`${inputCls} flex-1 min-w-0`}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addRule}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-dashed border-purple-300/30 bg-purple-500/[0.06] hover:bg-purple-500/15 hover:border-purple-300/50 text-xs font-semibold text-purple-200 hover:text-white transition"
          >
            <FiPlus className="w-3.5 h-3.5" />
            Add Filter Rule
          </button>

        </Section>

        {/* Exacerbation Filter */}
        <Section
          title="Exacerbation Filter"
          compact={!sidebarExpanded}
          compactTitle="Exacerbation"
          icon={<MdOutlineMedication className="w-4 h-4 text-orange-300" />}
          accent="from-orange-300 via-amber-300 to-purple-400"
        >
          {hasExacFilter && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-500/15 border border-orange-300/30 text-[10px] font-semibold text-orange-200 self-start">
              Active
            </span>
          )}

          <div className="grid grid-cols-1 gap-2">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                From Date
              </span>
              <input
                type="date"
                value={exacFrom}
                onChange={(e) => setExacFrom(e.target.value)}
                className={inputDateCls}
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                To Date
              </span>
              <input
                type="date"
                value={exacTo}
                onChange={(e) => setExacTo(e.target.value)}
                className={inputDateCls}
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                Count
              </span>
              <div className="flex gap-1.5">
                <div className="relative w-16 shrink-0">
                  <select
                    value={exacOperator}
                    onChange={(e) => setExacOperator(e.target.value)}
                    className={selectCls}
                  >
                    <option value="=" className="bg-[#0b0820] text-slate-100">=</option>
                    <option value=">" className="bg-[#0b0820] text-slate-100">{">"}</option>
                    <option value="<" className="bg-[#0b0820] text-slate-100">{"<"}</option>
                    <option value=">=" className="bg-[#0b0820] text-slate-100">{">="}</option>
                    <option value="<=" className="bg-[#0b0820] text-slate-100">{"<="}</option>
                  </select>
                  <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                </div>
                <input
                  type="number"
                  placeholder="e.g. 2"
                  value={exacCount}
                  onChange={(e) => setExacCount(e.target.value)}
                  className={`${inputCls} flex-1`}
                />
              </div>
            </label>
          </div>
        </Section>

        <div className={`grid gap-2 ${sidebarExpanded ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-5 lg:grid-cols-1"}`}>
          <button
            onClick={clearFilters}
            className={`inline-flex items-center gap-1.5 rounded-xl bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 hover:border-rose-400/50 font-medium text-rose-200 hover:text-white transition ${
              sidebarExpanded
                ? "justify-center px-3 py-2 text-xs"
                : "min-h-[58px] flex-col justify-center px-2 py-2 text-[10px]"
            }`}
            title="Clear Filters"
          >
            <FiX className="w-3.5 h-3.5" />
            <span>{sidebarExpanded ? "Clear Filters" : "Clear"}</span>
          </button>
          <button
            onClick={exportCSV}
            className={`group relative inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden ${
              sidebarExpanded
                ? "justify-center px-3 py-2 text-xs"
                : "min-h-[58px] flex-col justify-center px-2 py-2 text-[10px]"
            }`}
            title="Export CSV"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <FiDownload className="w-3.5 h-3.5 relative" />
            <span className="relative">{sidebarExpanded ? "Export CSV" : "CSV"}</span>
          </button>
        </div>
            </div>
          </aside>

          <main className="min-w-0">

        {/* Results */}
        <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 opacity-70 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.04] via-transparent to-blue-500/[0.04] pointer-events-none" />

          <div className="relative">
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
                <FiUser className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <div className="min-w-0">
                <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">
                  Results
                </h2>
                <p className="text-[10px] text-slate-400 leading-tight">
                  Click a row to open the patient detail
                </p>
              </div>
              <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-300/30 text-[10px] font-semibold text-emerald-200">
                {results.length} matches
              </span>
            </div>

            {/* Coordinator-notes search status — never a silent partial result */}
            {activeNotesTerm && coordProgress && (
              <div className="px-4 py-2 border-b border-white/5 text-[11px] text-purple-200">
                Searching coordinator notes… {coordProgress.done.toLocaleString()}/
                {coordProgress.total.toLocaleString()} patients. Showing legacy-notes matches until it finishes.
              </div>
            )}
            {activeNotesTerm && coordNotes?.failed.length > 0 && (
              <div className="px-4 py-2 border-b border-white/5 text-[11px] text-amber-200">
                Coordinator notes couldn't be searched for {coordNotes.failed.length} patient
                {coordNotes.failed.length === 1 ? "" : "s"}. Their legacy notes were still searched.
              </div>
            )}

            {/* Column headers */}
            <div className="hidden md:grid grid-cols-[2fr_1.5fr_1.2fr_2fr_0.7fr] px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400 border-b border-white/5 bg-slate-950/30">
              <div>Patient</div>
              <div>Diagnosis</div>
              <div>Status</div>
              <div>Study</div>
              <div>Age</div>
            </div>

            <div className="divide-y divide-white/5 min-h-[360px] lg:min-h-[520px] max-h-[70vh] lg:max-h-[calc(100vh-11.25rem)] overflow-y-auto">
              {results.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center mb-3">
                    <FiSearch className="w-5 h-5 text-purple-300" />
                  </div>
                  <h3 className="text-sm font-semibold text-white">No patients match</h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Try adjusting your filters or clearing them.
                  </p>
                </div>
              ) : (
                results.map((p) => (
                  <div
                    key={`${p.token}-${p.source}`}
                    onClick={() =>
                      navigate(
                        `/coordinator/patient/${encodeURIComponent(
                          p.token
                        )}?src=${p.source}`,
                        { state: { from: "query" } }
                      )
                    }
                    className="grid grid-cols-1 md:grid-cols-[2fr_1.5fr_1.2fr_2fr_0.7fr] gap-2 md:gap-3 px-4 py-2.5 hover:bg-white/[0.04] cursor-pointer transition group"
                  >
                    {/* Patient */}
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[10px] font-bold text-white ring-1 ring-white/20 shrink-0">
                        {(p.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate group-hover:text-purple-100 transition">
                          {p.name || "Unnamed"}
                        </div>
                        <div className="text-[10px] font-mono text-purple-300/80 truncate">
                          {p.mrn || "—"}
                        </div>
                      </div>
                    </div>

                    {/* Diagnosis */}
                    <div className="flex items-center min-w-0">
                      {p.diagnosis?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {p.diagnosis.map((dx) => (
                            <span
                              key={dx}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border backdrop-blur-md ${getDxToneCls(
                                dx
                              )}`}
                            >
                              {diagnosisLabel(dx)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500 italic">
                          Not Mentioned
                        </span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      {p.status ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border backdrop-blur-md ${getStatusToneCls(
                            p.status
                          )}`}
                        >
                          {statusLabel(p.status)}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500 italic">—</span>
                      )}
                    </div>

                    {/* Study */}
                    <div className="text-xs text-slate-200 truncate flex items-center">
                      {p.study || <span className="text-slate-500">—</span>}
                    </div>

                    {/* Age */}
                    <div className="text-xs font-mono text-slate-200 tabular-nums flex items-center">
                      {p.age}
                    </div>

                    {/* Where a notes / not-eligible search matched (only while one is active) */}
                    {foundInFor(p) && (
                      <div className="md:col-span-5 -mt-1 flex items-start gap-1.5 text-[11px] text-slate-300">
                        <FiFileText className="w-3 h-3 mt-0.5 shrink-0 text-purple-300" />
                        <span className="min-w-0">
                          <span className="font-semibold text-purple-200">{foundInFor(p).label}:</span>{" "}
                          {foundInFor(p).snippet}
                        </span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
          </main>
        </div>

        <div className="h-6" />
      </div>
    </div>
  );
};

export default PatientQueryPage;
